import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiChevronDown,
  FiLoader,
  FiMessageSquare,
  FiRotateCcw,
  FiSend,
  FiUserPlus,
  FiX,
} from 'react-icons/fi';

import {
  type Discussion,
  type Colleague,
  type DiscussionRequestIntent,
  discussionMatchesLocation,
  discussionsQueryKeys,
  resolveRequestIntent,
  useClinicalColleagues,
  useDiscussionConfig,
  useDiscussionThread,
  useDiscussions,
  useCreateDiscussion,
  useReplyDiscussion,
  useUpdateDiscussionStatus,
} from '../../../helpers/api/discussions/discussions';
import { useMe } from '../../../helpers/api/users/users';
import { useCollaboration } from './CollaborationContext';
import { buildStudyTarget, resolveCollaborationTargetLabel, type CollaborationTarget } from './collaboration-types';
import { formatCollaborationRelativeTime, formatDiscussionLocationLabel } from './collaboration-labels';
import ConversationCard, { StatusBadge } from './ConversationCard';
import ReviewReplyComposer from './ReviewReplyComposer';
import StructuredAnswerBlock from './StructuredAnswerBlock';

type CollaborationPanelProps = {
  studyInstanceUid: string;
};

type PendingMessage = {
  id: string;
  content: string;
};

function formatWhen(iso: string): string {
  return formatCollaborationRelativeTime(iso);
}

function authorInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function ChatAvatar({ name, isMine }: { name: string; isMine: boolean }) {
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
        isMine
          ? 'bg-blue-600 text-white'
          : 'bg-slate-200 text-slate-700 dark:bg-gray-700 dark:text-gray-100'
      }`}
      title={name}
      aria-hidden
    >
      {authorInitials(name)}
    </div>
  );
}

function ChatMessageRow({
  authorName,
  content,
  createdAt,
  isMine,
  pending = false,
  answerPayload,
  isOfficial = false,
}: {
  authorName: string;
  content: string;
  createdAt?: string;
  isMine: boolean;
  pending?: boolean;
  answerPayload?: Discussion['answer_payload'];
  isOfficial?: boolean;
}) {
  const { t } = useTranslation('ClinicalCollaboration');

  return (
    <div
      className={`flex items-end gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}
    >
      <ChatAvatar name={isMine ? t('you') : authorName} isMine={isMine} />
      <div
        className={`flex max-w-[78%] flex-col gap-0.5 ${
          isMine ? 'items-end' : 'items-start'
        }`}
      >
        {!isMine && (
          <span className="px-1 text-[10px] font-medium text-slate-500 dark:text-gray-400">
            {authorName}
          </span>
        )}
        <div
          className={`rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm ${
            isMine
              ? pending
                ? 'rounded-br-sm bg-blue-400/85 text-white'
                : 'rounded-br-sm bg-blue-600 text-white'
              : 'rounded-bl-sm bg-slate-100 text-slate-800 dark:border dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'
          }`}
        >
          <p className="whitespace-pre-wrap break-words">{content}</p>
          {answerPayload && (
            <StructuredAnswerBlock
              answer={answerPayload}
              isOfficial={isOfficial}
              embeddedInMineBubble={isMine}
            />
          )}
        </div>
        <span className="px-1 text-[10px] text-slate-400 dark:text-gray-500">
          {createdAt ? formatWhen(createdAt) : pending ? '…' : ''}
        </span>
      </div>
    </div>
  );
}

function isContextualTarget(nextTarget: CollaborationTarget | null): boolean {
  return nextTarget != null && nextTarget.type !== 'STUDY';
}

function resolveLocationTarget(
  scope: 'location' | 'all',
  viewerLocation: CollaborationTarget | null,
  target: CollaborationTarget | null
): CollaborationTarget | null {
  if (scope !== 'location') return null;
  return viewerLocation ?? (isContextualTarget(target) ? target : null);
}

function ColleaguePickerPanel({
  colleagues,
  selectedIds,
  onChange,
  onClose,
}: {
  colleagues: Colleague[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('ClinicalCollaboration');

  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div className="mb-3 rounded-xl border border-slate-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/80">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-gray-700">
        <p className="text-xs font-semibold text-slate-600 dark:text-gray-300">
          {t('notifyColleagues')}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-400 hover:bg-slate-200 dark:hover:bg-gray-700"
        >
          <FiX size={14} />
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto p-1">
        {colleagues.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-slate-400">{t('noColleagues')}</p>
        ) : (
          colleagues.map((c) => {
            const checked = selectedIds.includes(c.id);
            return (
              <label
                key={c.id}
                className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                  checked
                    ? 'bg-blue-50 dark:bg-blue-950/30'
                    : 'hover:bg-white dark:hover:bg-gray-700/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(c.id)}
                  className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800 dark:text-gray-100">
                    {c.display_name}
                  </p>
                  <p className="text-xs capitalize text-slate-400">{c.role}</p>
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function CollaborationPanel({ studyInstanceUid }: CollaborationPanelProps) {
  const { t } = useTranslation('ClinicalCollaboration');
  const queryClient = useQueryClient();
  const {
    isOpen,
    target,
    viewerLocation,
    scope,
    initialDiscussionId,
    locationSeq,
    closeCollaboration,
    setScope,
    clearInitialDiscussionId,
  } = useCollaboration();

  const studyTarget = buildStudyTarget(studyInstanceUid);
  const locationTarget = resolveLocationTarget(scope, viewerLocation, target);
  const filterByLocation = scope === 'location' && locationTarget != null;
  const activeTarget =
    scope === 'all' ? studyTarget : (locationTarget ?? studyTarget);
  const canToggleScope = Boolean(viewerLocation) || isContextualTarget(target);
  const inLocationScope = scope === 'location' && locationTarget != null;

  const [selectedDiscussionId, setSelectedDiscussionId] = useState<string | null>(null);
  const [composeText, setComposeText] = useState('');
  const [requestIntent, setRequestIntent] = useState<DiscussionRequestIntent>('READ_ONLY');
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
  const [showColleaguePicker, setShowColleaguePicker] = useState(false);
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const listFilters = filterByLocation && locationTarget
    ? { targetType: locationTarget.type, targetRef: locationTarget.ref }
    : undefined;

  const {
    data: listResponse,
    isLoading: isLoadingList,
    isFetching: isFetchingList,
  } = useDiscussions(studyInstanceUid, listFilters, isOpen);

  const {
    data: thread,
    isLoading: isLoadingThread,
    isFetching: isFetchingThread,
  } = useDiscussionThread(selectedDiscussionId ?? undefined, studyInstanceUid);

  const { data: colleagues = [] } = useClinicalColleagues(isOpen);
  const { data: discussionConfig } = useDiscussionConfig(isOpen);

  const { data: currentUser } = useMe({ enabled: isOpen });

  const createMutation = useCreateDiscussion(studyInstanceUid);
  const replyMutation = useReplyDiscussion(
    studyInstanceUid,
    selectedDiscussionId ?? ''
  );
  const statusMutation = useUpdateDiscussionStatus(
    studyInstanceUid,
    selectedDiscussionId ?? ''
  );

  const threads = useMemo(() => {
    const data = listResponse?.data ?? [];
    if (!filterByLocation || locationTarget == null) return data;
    return data.filter((item) =>
      discussionMatchesLocation(item, locationTarget.type, locationTarget.ref)
    );
  }, [listResponse, filterByLocation, locationTarget]);

  const { activeThreads, closedThreads } = useMemo(() => {
    const sortByActivity = (a: Discussion, b: Discussion) => {
      const ta = new Date(a.last_reply_at ?? a.created_at).getTime();
      const tb = new Date(b.last_reply_at ?? b.created_at).getTime();
      return tb - ta;
    };
    const active: Discussion[] = [];
    const closed: Discussion[] = [];
    for (const item of threads) {
      if (item.status === 'CLOSED') closed.push(item);
      else active.push(item);
    }
    active.sort(sortByActivity);
    closed.sort(sortByActivity);
    return { activeThreads: active, closedThreads: closed };
  }, [threads]);

  const inThreadView = selectedDiscussionId != null;
  const isPosting =
    createMutation.isPending || replyMutation.isPending || statusMutation.isPending;
  const isThreadClosed = thread?.status === 'CLOSED';
  const isThreadAuthor =
    Boolean(currentUser?.id) && thread?.author?.id === currentUser?.id;

  const isMessageFromCurrentUser = (authorId?: string) =>
    Boolean(currentUser?.id) && authorId === currentUser?.id;

  const threadIntent = thread ? resolveRequestIntent(thread) : 'READ_ONLY';
  const normalizeUserId = (id?: string | null) => id?.toLowerCase() ?? '';
  const isReviewRecipient =
    Boolean(currentUser?.id) &&
    (thread?.recipients ?? []).some(
      (r) => normalizeUserId(r.user_id) === normalizeUserId(currentUser?.id)
    );
  const llmEnabled = Boolean(discussionConfig?.llm_suggest_enabled);
  const isReadOnlyThread = threadIntent === 'READ_ONLY';
  const canReplyInThread =
    inThreadView && !isThreadClosed && (!isReadOnlyThread || isThreadAuthor);
  const showReviewerWaitingHint =
    inThreadView &&
    threadIntent === 'REVIEW' &&
    thread?.status === 'OPEN' &&
    isThreadAuthor &&
    !isReviewRecipient;
  const showListSkeleton =
    (isLoadingList && !listResponse) ||
    (filterByLocation && isFetchingList && threads.length === 0 && !listResponse);
  const showThreadSkeleton = isLoadingThread && !thread;

  useEffect(() => {
    if (!isOpen) {
      setSelectedDiscussionId(null);
      return;
    }

    if (initialDiscussionId) {
      setSelectedDiscussionId(initialDiscussionId);
      clearInitialDiscussionId();
      setComposeText('');
      setRequestIntent('READ_ONLY');
      setSelectedRecipientIds([]);
      setShowColleaguePicker(false);
      setPendingMessages([]);
      return;
    }

    setSelectedDiscussionId(null);
    setComposeText('');
    setRequestIntent('READ_ONLY');
    setSelectedRecipientIds([]);
    setShowColleaguePicker(false);
    setPendingMessages([]);
  }, [
    isOpen,
    initialDiscussionId,
    locationSeq,
    scope,
    locationTarget?.type,
    locationTarget?.ref,
    clearInitialDiscussionId,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    void queryClient.invalidateQueries({
      queryKey: discussionsQueryKeys.list(studyInstanceUid),
    });
  }, [isOpen, locationSeq, scope, locationTarget?.type, locationTarget?.ref, queryClient, studyInstanceUid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.replies?.length, pendingMessages.length, inThreadView]);

  const handlePost = () => {
    const content = composeText.trim();
    if (!content || isPosting) return;

    if (!inThreadView && requestIntent === 'REVIEW' && selectedRecipientIds.length === 0) {
      setShowColleaguePicker(true);
      return;
    }

    setComposeText('');

    const pendingId = `pending-${Date.now()}`;
    if (inThreadView) {
      setPendingMessages((prev) => [...prev, { id: pendingId, content }]);
    }

    const run = async () => {
      try {
        if (inThreadView && selectedDiscussionId) {
          await replyMutation.mutateAsync({ content });
        } else {
          const created = await createMutation.mutateAsync({
            request_intent: requestIntent,
            discussion_type: requestIntent === 'REVIEW' ? 'OPINION_REQUEST' : 'COMMENT',
            content,
            target_type: activeTarget.type,
            target_ref: activeTarget.ref,
            study_instance_uid: studyInstanceUid,
            recipient_user_ids:
              requestIntent === 'REVIEW' ? selectedRecipientIds : undefined,
          });
          if (created?.id) setSelectedDiscussionId(created.id);
          setRequestIntent('READ_ONLY');
          setSelectedRecipientIds([]);
          setShowColleaguePicker(false);
        }
      } finally {
        setPendingMessages((prev) => prev.filter((p) => p.id !== pendingId));
      }
    };

    void run();
  };

  const handleToggleThreadStatus = () => {
    if (!selectedDiscussionId || !isThreadAuthor) return;
    void statusMutation.mutateAsync(isThreadClosed ? 'OPEN' : 'CLOSED');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label={t('close')}
        className="absolute inset-0 bg-black/30 backdrop-blur-[1px]"
        onClick={closeCollaboration}
      />

      <aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl dark:bg-gray-900">
        <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900">
          {inThreadView ? (
            <button
              type="button"
              onClick={() => {
                setSelectedDiscussionId(null);
                setComposeText('');
                setPendingMessages([]);
              }}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-gray-400 dark:hover:bg-gray-800"
              aria-label={t('backToList')}
            >
              <FiArrowLeft size={18} />
            </button>
          ) : (
            <FiMessageSquare className="ml-1 text-blue-600 dark:text-blue-400" size={18} />
          )}
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {inThreadView
                ? t('thread')
                : inLocationScope
                  ? t('threadsHere')
                  : t('threads')}
            </h2>
            {!inThreadView && (
              <p className="truncate text-xs text-slate-500 dark:text-gray-400">
                {resolveCollaborationTargetLabel(activeTarget.label)}
              </p>
            )}
            {inThreadView && thread?.target_label && (
              <p className="truncate text-xs text-blue-600 dark:text-blue-400">
                {formatDiscussionLocationLabel(thread)}
              </p>
            )}
          </div>
          {(isFetchingList || isFetchingThread) && (
            <FiLoader className="animate-spin text-slate-400" size={14} />
          )}
          <button
            type="button"
            onClick={closeCollaboration}
            className="rounded-lg p-2 text-gray-500 hover:bg-slate-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <FiX size={18} />
          </button>
        </header>

        {!inThreadView && (
          <div className="flex shrink-0 items-center gap-2 border-b border-slate-100 px-3 py-2 dark:border-gray-800">
            {canToggleScope ? (
              <button
                type="button"
                onClick={() => setScope(scope === 'location' ? 'all' : 'location')}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  scope === 'location'
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                {scope === 'location' ? t('scope.location') : t('scope.all')}
                <FiChevronDown size={12} className="opacity-60" />
              </button>
            ) : (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 dark:bg-gray-800 dark:text-gray-300">
                {t('scope.all')}
              </span>
            )}
            <span className="truncate text-[11px] text-slate-500 dark:text-gray-400">
              {resolveCollaborationTargetLabel(activeTarget.label)}
            </span>
            <span className="ml-auto shrink-0 text-[11px] text-slate-400 dark:text-gray-500">
              {t('threadCount', { count: threads.length })}
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          {inThreadView ? (
            <div className="flex min-h-full flex-col gap-4 p-3">
              {showThreadSkeleton ? (
                <div className="space-y-3 py-4">
                  <div className="h-12 animate-pulse rounded-xl bg-slate-100 dark:bg-gray-800" />
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-gray-800" />
                </div>
              ) : thread ? (
                <>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-slate-500 dark:text-gray-300">
                          {threadIntent === 'REVIEW'
                            ? t('intent.review')
                            : t('intent.readOnly')}
                        </span>
                        <StatusBadge status={thread.status} />
                      </div>
                      {isThreadAuthor && threadIntent === 'REVIEW' && (
                        <button
                          type="button"
                          onClick={handleToggleThreadStatus}
                          disabled={statusMutation.isPending}
                          className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                            isThreadClosed
                              ? 'bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-gray-700 dark:text-gray-200'
                              : 'bg-emerald-600 text-white hover:bg-emerald-700'
                          }`}
                        >
                          {statusMutation.isPending ? (
                            <FiLoader className="animate-spin" size={12} />
                          ) : isThreadClosed ? (
                            <FiRotateCcw size={12} />
                          ) : (
                            <FiCheckCircle size={12} />
                          )}
                          {isThreadClosed ? t('reopenConversation') : t('closeConversation')}
                        </button>
                      )}
                    </div>
                    {thread.target_label && (
                      <p className="mt-1.5 text-xs font-medium text-blue-600 dark:text-blue-400">
                        {formatDiscussionLocationLabel(thread)}
                      </p>
                    )}
                  </div>
                  {isThreadClosed && (
                    <p className="rounded-lg bg-slate-100 px-3 py-2 text-center text-xs text-slate-500 dark:bg-gray-800 dark:text-gray-400">
                      {t('threadClosedHint')}
                    </p>
                  )}
                  <ChatMessageRow
                    authorName={thread.author.display_name}
                    content={thread.content}
                    createdAt={thread.created_at}
                    isMine={isMessageFromCurrentUser(thread.author.id)}
                  />
                  {(thread.replies ?? []).map((reply) => (
                    <ChatMessageRow
                      key={reply.id}
                      authorName={reply.author.display_name}
                      content={reply.content}
                      createdAt={reply.created_at}
                      isMine={isMessageFromCurrentUser(reply.author.id)}
                      answerPayload={reply.answer_payload}
                      isOfficial={reply.id === thread.official_answer_id}
                    />
                  ))}
                  {pendingMessages.map((pending) => (
                    <ChatMessageRow
                      key={pending.id}
                      authorName={t('you')}
                      content={pending.content}
                      isMine
                      pending
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </>
              ) : null}
            </div>
          ) : (
            <div className="p-2" key={`${scope}:${locationTarget?.type ?? 'study'}:${locationTarget?.ref ?? 'all'}:${locationSeq}`}>
              {showListSkeleton ? (
                <div className="space-y-2 py-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-14 animate-pulse rounded-xl bg-slate-100 dark:bg-gray-800"
                    />
                  ))}
                </div>
              ) : threads.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                  <FiMessageSquare className="mb-3 text-3xl text-slate-200 dark:text-gray-700" />
                  <p className="text-sm font-medium text-slate-600 dark:text-gray-300">
                    {inLocationScope ? t('emptyLocation') : t('empty')}
                  </p>
                  <p className="mt-1 text-xs text-slate-400 dark:text-gray-500">
                    {scope === 'location' && !locationTarget
                      ? t('emptyLocationNoContext')
                      : t('emptyHint')}
                  </p>
                </div>
              ) : (
                <div className="space-y-4 px-1">
                  {activeThreads.length > 0 && (
                    <section>
                      <h3 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
                        {t('activeConversations', { count: activeThreads.length })}
                      </h3>
                      <ul className="space-y-2">
                        {activeThreads.map((discussion) => (
                          <li key={discussion.id}>
                            <ConversationCard
                              discussion={discussion}
                              onClick={() => setSelectedDiscussionId(discussion.id)}
                            />
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {closedThreads.length > 0 && (
                    <section>
                      <h3 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
                        {t('closedConversations', { count: closedThreads.length })}
                      </h3>
                      <ul className="space-y-2">
                        {closedThreads.map((discussion) => (
                          <li key={discussion.id}>
                            <ConversationCard
                              discussion={discussion}
                              onClick={() => setSelectedDiscussionId(discussion.id)}
                            />
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-slate-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
          {inThreadView && isThreadClosed ? (
            <p className="py-2 text-center text-xs text-slate-500 dark:text-gray-400">
              {t('cannotReplyClosed')}
            </p>
          ) : inThreadView && thread && isReadOnlyThread && !isThreadAuthor ? (
            <p className="py-2 text-center text-xs text-slate-500 dark:text-gray-400">
              {t('readOnlyNoReply')}
            </p>
          ) : (
            <>
          {showReviewerWaitingHint && (
            <p className="mb-2 rounded-lg border border-slate-200 bg-gray-50 px-3 py-2 text-xs text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              {t('reviewWaitingForRecipient')}
            </p>
          )}
          {!inThreadView && showColleaguePicker && (
            <ColleaguePickerPanel
              colleagues={colleagues}
              selectedIds={selectedRecipientIds}
              onChange={setSelectedRecipientIds}
              onClose={() => setShowColleaguePicker(false)}
            />
          )}

          {!inThreadView && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setRequestIntent('READ_ONLY')}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  requestIntent === 'READ_ONLY'
                    ? 'bg-slate-200 text-slate-800 dark:bg-gray-700 dark:text-gray-100'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-400'
                }`}
              >
                {t('intent.readOnly')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setRequestIntent('REVIEW');
                  setShowColleaguePicker(true);
                }}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  requestIntent === 'REVIEW'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-400'
                }`}
              >
                {t('intent.review')}
              </button>
              <button
                type="button"
                onClick={() => setShowColleaguePicker((v) => !v)}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  selectedRecipientIds.length > 0 || showColleaguePicker
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-400'
                }`}
              >
                <FiUserPlus size={12} />
                {selectedRecipientIds.length > 0
                  ? t('notifying', { count: selectedRecipientIds.length })
                  : t('notify')}
              </button>
            </div>
          )}

          {canReplyInThread && selectedDiscussionId && threadIntent === 'REVIEW' ? (
            <ReviewReplyComposer
              threadId={selectedDiscussionId}
              mode="review"
              isPosting={isPosting}
              llmEnabled={llmEnabled}
              onReply={async (payload) => {
                if (!selectedDiscussionId) return;
                await replyMutation.mutateAsync(payload);
              }}
            />
          ) : canReplyInThread && selectedDiscussionId && isReadOnlyThread ? (
            <div className="flex items-end gap-2">
              <textarea
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isPosting}
                placeholder={t('placeholder.readOnlyFollowUp')}
                rows={2}
                className="min-h-[44px] flex-1 resize-none rounded-xl border border-slate-200 bg-gray-50 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handlePost}
                disabled={isPosting || !composeText.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
                title={t('post')}
              >
                {isPosting ? (
                  <FiLoader className="animate-spin" size={16} />
                ) : (
                  <FiSend size={16} />
                )}
              </button>
            </div>
          ) : !inThreadView ? (
            <div className="flex items-end gap-2">
              <textarea
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isPosting}
                placeholder={
                  requestIntent === 'REVIEW'
                    ? t('placeholder.review')
                    : t('placeholder.readOnly')
                }
                rows={2}
                className="min-h-[44px] flex-1 resize-none rounded-xl border border-slate-200 bg-gray-50 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handlePost}
                disabled={isPosting || !composeText.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
                title={t('post')}
              >
                {isPosting ? (
                  <FiLoader className="animate-spin" size={16} />
                ) : (
                  <FiSend size={16} />
                )}
              </button>
            </div>
          ) : null}
            </>
          )}
        </footer>
      </aside>
    </div>
  );
}
